import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { FC } from 'react';
import { BoxCard } from '../../../../../core/BoxCard';
import { getRatioGain, getRatioGainColor } from '../../../../../utils/format';
import { Discipline } from '../../../services/enum';
import { Course, Prono } from '../../../services/type';
import StarIcon from '@mui/icons-material/Star';


interface Props {
    prono: Prono;
    course: Course;
}

const getPercentileDot = (score: number, isMobile: boolean) => {
    if (score < 0.5) return null; // seul le haut du panier est signalé

    const color = score >= 0.95 ? 'success.main' : score >= 0.80 ? 'primary.main' : 'grey.500';

    return (
        <Box display="flex" alignItems="center" gap={0.3}>
            <Box width={6} height={6} borderRadius="50%" bgcolor={color} />
            <Typography fontSize={isMobile ? 9 : 10} color={color} fontWeight={600}>
                {`Top ${Math.round((1 - score) * 100)}%`}
            </Typography>
        </Box>
    );
};

const getStars = (score: number, isMobile: boolean) => {
    // Seuils calés sur le percentile — seul le haut du panier a des étoiles
    let nbStars = 0;
    if (score >= 0.98) nbStars = 5;      // top 2%
    else if (score >= 0.95) nbStars = 4; // top 5%
    else if (score >= 0.90) nbStars = 3; // top 10%
    else if (score >= 0.80) nbStars = 2; // top 20%
    else if (score >= 0.65) nbStars = 1; // top 35%
    // en dessous de 0.65 → 0 étoile, la grande majorité des jockeys

    if (nbStars === 0) return null;
    console.log("nb star", nbStars)
    return (
        <Box
            display={'flex'}
            flexDirection={'row'}
            position={'absolute'}
            top={32}
            flexWrap="wrap"
            width={'50px'}
            justifyContent={'center'}
            alignSelf={'center'}
        >
            {[...Array(nbStars)].map((_, index) => (
                <StarIcon
                    key={index}
                    sx={{ fontSize: isMobile ? 12 : 16, color: 'gold' }}
                />
            ))}
        </Box>
    );
};

const getJockeyTooltip = (
    frequencePodium: number,
    nbRaceFromPodium: number,
    zScore: number
) => {
    return (
        <Box display={'flex'} flexDirection={'column'}>
            {frequencePodium > 0 && (
                <Typography color="white" variant="subtitle1">
                    {`Fréquence podiums : ${(1 / frequencePodium).toFixed(2)}`}
                </Typography>
            )}
            <Typography color="white" variant="subtitle1">
                {`Courses depuis podium : ${nbRaceFromPodium}`}
            </Typography>
            <Typography color="white" variant="subtitle1">
                {`Score écart : ${zScore.toFixed(2)}`}
            </Typography>
        </Box>
    );
};

export const Header: FC<Props> = ({ prono, course }) => {
    const ratio = getRatioGain(
        prono.data.price / 100,
        course.plafond || undefined,
        course.recul || undefined
    );

    return (
        <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }} minHeight={"50px"} display={'flex'} alignItems={'center'}>
            <Box display={'flex'} flexDirection={'row'} gap={1} mb={2}>
                <BoxCard>
                    <Typography color="white" variant="subtitle2">
                        {`${prono.data.age} ans`}
                    </Typography>
                    <Typography color="white" variant="subtitle2">
                        {prono.data.sexe.toLowerCase()}
                    </Typography>
                </BoxCard>
                <BoxCard>
                    <Typography color="white" variant="subtitle2">
                        {course.discipline === Discipline.ATTELE ||
                            course.discipline === Discipline.MONTE
                            ? `${(prono.data.price / 100000).toFixed(1)}k€`
                            : `Gains: ${(prono.data.price / 100).toLocaleString('fr-FR')}€`}
                    </Typography>
                    {ratio != null && (
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                color: getRatioGainColor(ratio * 100, true),
                            }}
                        >
                            {`Ratio: ${Math.round(ratio * 100)}%`}
                        </Typography>
                    )}
                </BoxCard>
                <BoxCard
                    tooltip={getJockeyTooltip(
                        prono.preparation.jockeyFrequencePodium,
                        prono.preparation.jockeyNbRaceFromLastPodium,
                        prono.preparation.jockeyZScore
                    )}
                >
                    <Typography
                        color="white"
                        variant="subtitle2"
                        fontSize={prono.data.jockey.length > 15 ? 12 : 14}
                    >
                        {prono.data.jockey
                            .toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </Typography>
                    {getStars(prono.preparation.jockeyScoreClassement, false)}
                </BoxCard>
                <BoxCard>
                    <Typography
                        color="white"
                        variant="subtitle2"
                        fontSize={prono.data.entraineur.length > 15 ? 12 : 14}
                    >
                        {prono.data.entraineur
                            .toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())}
                    </Typography>
                    {getStars(prono.context.entraineurScoreClassement, false)}
                </BoxCard>
                {prono.data.eleveur && (
                    <BoxCard>
                        <Typography
                            color="white"
                            variant="subtitle2"
                            fontSize={prono.data.eleveur.length > 15 ? 12 : 14}
                        >
                            {prono.data.eleveur
                                .toLowerCase()
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                        </Typography>
                        {/* {getStars(prono.tags, EntityEnum.GROWER, false)} */}
                    </BoxCard>
                )}
            </Box>
        </Box>
    );
};
